import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "../../api";
import { Loader2, CreditCard, CheckCircle2, AlertCircle, Zap, ChevronDown, FileDown, Mail } from "lucide-react";
import { toast } from "../../hooks/use-toast";

export default function StripeTab() {
  const [connect, setConnect] = useState(null); // {connected, account_id, details_submitted}
  const [sub, setSub] = useState(null); // {status, monthly_fee}
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showLegacy, setShowLegacy] = useState(false);
  const [keys, setKeys] = useState({ stripe_pk: "", stripe_sk: "" });
  const [invoices, setInvoices] = useState([]);
  const [stmtMonth, setStmtMonth] = useState("");
  const [stmtEmail, setStmtEmail] = useState("");
  const [stmtMonths, setStmtMonths] = useState([]);

  const downloadStatement = async (month) => {
    const m = typeof month === "string" ? month : stmtMonth;
    const busyKey = typeof month === "string" ? `stmt-${m}` : "stmt";
    setBusy(busyKey);
    try {
      const res = await api.get(`/business/payout-statement.pdf${m ? `?month=${m}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payout-statement-${m || "all-time"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast({ title: "Error", description: "Could not generate statement", variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const emailStatement = async () => {
    if (!stmtEmail.trim()) { toast({ title: "Enter an email address", variant: "destructive" }); return; }
    setBusy("stmt-email");
    try {
      const { data } = await api.post("/business/payout-statement/email", { email: stmtEmail.trim(), month: stmtMonth || null });
      toast({ title: "Statement emailed", description: `Sent to ${data.to} (${data.period}).` });
    } catch (err) {
      toast({ title: "Couldn't email statement", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const openInvoicePdf = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast({ title: "Error", description: "Could not open invoice", variant: "destructive" }); }
  };

  const refresh = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/business/connect/status"),
        api.get("/business/subscription/status"),
      ]);
      setConnect(c.data);
      setSub(s.data);
      api.get("/business/payout").then(({ data }) => setPayout(data)).catch(() => {});
      api.get("/business/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
      api.get("/business/statement-months").then(({ data }) => setStmtMonths(data.months || [])).catch(() => {});
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    // Handle Stripe redirect returns
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "return") {
      toast({ title: "Welcome back", description: "Checking your Stripe connection…" });
      setTimeout(refresh, 800);
    }
  }, [refresh]);

  const onboard = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post("/business/connect/onboard", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast({ title: "Stripe Connect unavailable", description: err?.response?.data?.detail || "Connect is not enabled yet.", variant: "destructive" });
      setBusy("");
    }
  };

  const subscribe = async () => {
    setBusy("sub");
    try {
      const { data } = await api.post("/business/subscription/checkout", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast({ title: "Could not start subscription", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
      setBusy("");
    }
  };

  const saveLegacy = async (e) => {
    e.preventDefault();
    setBusy("legacy");
    try {
      await api.put("/business/stripe", keys);
      toast({ title: "Stripe keys saved" });
    } catch (err) {
      toast({ title: "Error", description: "Failed to save keys", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const requestPayout = async () => {
    setBusy("payout");
    try {
      const { data } = await api.post("/business/payout/request");
      toast({ title: "Payout requested", description: `We'll process £${data.amount.toFixed(2)} shortly.` });
      refresh();
    } catch (err) {
      toast({ title: "Couldn't request payout", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const connected = connect?.connected;
  const method = connect?.payout_method || "none";
  const payoutReady = !!connect?.payout_ready;
  const feePence = (fx) => (fx >= 1 ? `£${fx.toFixed(2)}` : `${Math.round(fx * 100)}p`);
  const feeLabel = payout
    ? `${payout.commission_pct}%${payout.commission_fixed ? ` + ${feePence(payout.commission_fixed)}` : ""} per ticket`
    : "6% + 30p per ticket";
  const methodCopy = {
    connect: "Stripe connected — ticket payments pay out directly to your Stripe account (minus the platform fee).",
    own_keys: "Using your own Stripe keys — ticket payments go straight to your Stripe account. Our platform commission on these sales is billed to you monthly (see Invoices below). Choose whether to absorb it or pass it to customers in Settings.",
    platform: "Approved by TicketsPro — we collect payments for you. Request a payout of your balance from the card below any time.",
  };

  return (
    <div className="max-w-[620px] space-y-6">
      {/* Payout readiness status */}
      <div className={`rounded-2xl p-5 border ${payoutReady ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`} data-testid="payout-readiness">
        <div className="flex items-start gap-3">
          {payoutReady
            ? <CheckCircle2 className="text-green-600 shrink-0 mt-0.5" size={20} />
            : <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={20} />}
          <div>
            <h3 className={`font-semibold ${payoutReady ? "text-green-800" : "text-amber-800"}`} data-testid="payout-readiness-title">
              {payoutReady ? "You're set to receive payouts" : "Connect Stripe to receive payouts"}
            </h3>
            <p className={`text-sm mt-0.5 ${payoutReady ? "text-green-700/90" : "text-amber-700/90"}`} data-testid="payout-readiness-method">
              {payoutReady ? (methodCopy[method] || "Payouts are enabled.")
                : "You can create and list events now, but ticket sales won't pay out until you connect a payout method below."}
            </p>
          </div>
        </div>
      </div>

      {/* Connect account */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="connect-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-[#eefafa] flex items-center justify-center"><CreditCard className="text-[#2ec9c9]" size={22} /></div>
          <div>
            <h3 className="font-semibold text-[#16243b]">Connect your Stripe account</h3>
            <p className="text-sm text-[#8a97a8]">Get paid directly. Our platform fee is {feeLabel} — no monthly subscription.</p>
          </div>
        </div>

        {connected ? (
          <div className="bg-green-50 text-green-700 rounded-xl p-3 flex items-center gap-2 text-sm mb-4" data-testid="connect-status-connected">
            <CheckCircle2 size={18} /> Stripe connected — payouts enabled. Sales pay out to you automatically, minus the {feeLabel} platform fee.
          </div>
        ) : connect?.account_id ? (
          <div className="bg-amber-50 text-amber-700 rounded-xl p-3 flex items-center gap-2 text-sm mb-4" data-testid="connect-status-pending">
            <AlertCircle size={18} /> Onboarding started but not finished. Click below to complete your Stripe setup.
          </div>
        ) : (
          <div className="bg-slate-50 text-[#5a6b80] rounded-xl p-3 flex items-center gap-2 text-sm mb-4" data-testid="connect-status-none">
            <AlertCircle size={18} /> Not connected yet. Connect Stripe to start receiving payouts.
          </div>
        )}

        <button onClick={onboard} disabled={busy === "connect"} data-testid="connect-stripe-btn"
          className="gp-btn-teal rounded-xl px-6 py-3 font-medium disabled:opacity-60 inline-flex items-center gap-2">
          {busy === "connect" ? <Loader2 className="animate-spin" size={18} /> : <Zap size={18} />}
          {connected ? "Manage Stripe account" : connect?.account_id ? "Finish Stripe setup" : "Connect Stripe"}
        </button>
      </div>

      {/* Approved / platform-collect banner */}
      {payout?.admin_approved && (
        <div className="bg-[#eef7ff] border border-[#d6e8fb] rounded-2xl p-5" data-testid="approved-banner">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="text-[#2a7de1]" size={20} />
            <h3 className="font-semibold text-[#16243b]">Approved by TicketsPro.app</h3>
          </div>
          <p className="text-sm text-[#5a6b80]">
            Your platform fee is <strong>{payout?.fee_status === "waived" ? "waived (free)" : feeLabel}</strong>.
            {payout.platform_collect ? " Payments are collected securely by TicketsPro.app — request a payout of your balance below any time." : ""}
          </p>
        </div>
      )}

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="invoices-card">
          <h3 className="font-semibold text-[#16243b] mb-3">Invoices</h3>
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2" data-testid={`invoice-${inv.id}`}>
                <div>
                  <span className="font-mono text-[#16243b]">{inv.number}</span>
                  <span className="text-[#8a97a8] ml-2">{inv.period || new Date(inv.issued_at).toLocaleDateString("en-GB")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#16243b] font-semibold">£{inv.amount.toFixed(2)}</span>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{inv.status}</span>
                  <a href={`${API}/invoices/${inv.id}/pdf`} onClick={(e) => { e.preventDefault(); openInvoicePdf(inv.id); }} className="text-[#1fb0b0] hover:underline cursor-pointer" data-testid={`invoice-pdf-${inv.id}`}>PDF</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payout visibility */}
      {payout && (
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="payout-card">
          <h3 className="font-semibold text-[#16243b] mb-1">Your payouts</h3>
          <p className="text-sm text-[#8a97a8] mb-4">Based on {payout.sales_count} paid ticket(s). Platform fee is {feeLabel}.</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#f6f8fb] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Gross sales</p><p className="text-lg font-bold text-[#16243b]">£{payout.gross_sales.toFixed(2)}</p></div>
            <div className="bg-[#f6f8fb] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Platform fee</p><p className="text-lg font-bold text-[#f47c7c]">−£{payout.platform_fee.toFixed(2)}</p></div>
            <div className="bg-[#eefafa] rounded-xl p-4"><p className="text-xs text-[#8a97a8]">Net after fee</p><p className="text-lg font-bold text-[#16243b]" data-testid="net-payout">£{payout.net_payout.toFixed(2)}</p></div>
          </div>

          {payout.platform_collect ? (
            <div className="mt-5 border-t border-gray-100 pt-5" data-testid="platform-payout">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs text-[#8a97a8]">Available to withdraw (collected by TicketsPro, net of {feeLabel} fee)</p>
                  <p className="text-2xl font-bold text-[#16243b]" data-testid="available-payout">£{(payout.available_payout ?? 0).toFixed(2)}</p>
                  {payout.paid_out > 0 && <p className="text-xs text-[#8a97a8] mt-0.5">£{payout.paid_out.toFixed(2)} already paid out</p>}
                </div>
                {payout.open_request ? (
                  <span className="px-4 py-2 rounded-full text-sm font-medium bg-amber-100 text-amber-700">Payout of £{payout.open_request.amount.toFixed(2)} requested</span>
                ) : (
                  <button onClick={requestPayout} disabled={busy === "payout" || (payout.available_payout ?? 0) <= 0} data-testid="request-payout-btn"
                    className="gp-btn-teal rounded-xl px-6 py-3 font-medium disabled:opacity-50 inline-flex items-center gap-2">
                    {busy === "payout" ? <Loader2 className="animate-spin" size={18} /> : null} Request payout
                  </button>
                )}
              </div>
              {(payout.requests || []).length > 0 && (
                <div className="mt-4 space-y-1">
                  {payout.requests.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm border-t border-gray-50 pt-2">
                      <span className="text-[#5a6b80]">£{r.amount.toFixed(2)} · {new Date(r.created_at).toLocaleDateString("en-GB")}</span>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.status === "paid" ? "bg-green-100 text-green-700" : r.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            !payout.stripe_connected && <p className="text-xs text-[#8a97a8] mt-3">Connect Stripe above to receive these payouts automatically.</p>
          )}
        </div>
      )}

      {/* Monthly payout statement (PDF) */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="payout-statement-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-[#eefafa] flex items-center justify-center"><FileDown className="text-[#2ec9c9]" size={22} /></div>
          <div>
            <h3 className="font-semibold text-[#16243b]">Payout statement</h3>
            <p className="text-sm text-[#8a97a8]">Download a PDF summary (gross − fees − refunds = net) for your accountant.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} data-testid="statement-month"
            className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          {stmtMonth
            ? <button onClick={() => setStmtMonth("")} className="text-xs text-[#8a97a8] hover:underline" data-testid="statement-alltime">All time</button>
            : <span className="text-xs text-[#8a97a8]">Leave blank for an all-time statement</span>}
          <button onClick={() => downloadStatement()} disabled={busy === "stmt"} data-testid="download-statement-btn"
            className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2 ml-auto">
            {busy === "stmt" ? <Loader2 className="animate-spin" size={16} /> : <FileDown size={16} />} Download statement (PDF)
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-gray-100">
          <Mail className="text-[#8a97a8]" size={16} />
          <input type="email" value={stmtEmail} onChange={(e) => setStmtEmail(e.target.value)} data-testid="statement-email"
            placeholder="accountant@email.com" className="flex-1 min-w-[180px] rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          <button onClick={emailStatement} disabled={busy === "stmt-email"} data-testid="email-statement-btn"
            className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2">
            {busy === "stmt-email" ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Email to accountant
          </button>
        </div>
        {stmtMonths.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100" data-testid="statement-history">
            <p className="text-sm font-medium text-[#16243b] mb-2">Statement history</p>
            <div className="space-y-1.5">
              {stmtMonths.map((m) => (
                <div key={m.month} className="flex items-center justify-between gap-3 rounded-lg bg-[#f6f8fb] px-3 py-2" data-testid={`statement-row-${m.month}`}>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-[#16243b]">{m.label}</span>
                    <span className="text-xs text-[#8a97a8] ml-2">Net £{m.net.toFixed(2)} · {m.tickets} ticket(s)</span>
                  </div>
                  <button onClick={() => downloadStatement(m.month)} disabled={busy === `stmt-${m.month}`} data-testid={`statement-download-${m.month}`}
                    className="shrink-0 text-[#1fb0b0] hover:underline text-sm font-medium inline-flex items-center gap-1">
                    {busy === `stmt-${m.month}` ? <Loader2 className="animate-spin" size={14} /> : <FileDown size={14} />} PDF
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Legacy keys (advanced fallback) */}
      <div className="bg-white rounded-2xl p-4 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <button onClick={() => setShowLegacy((v) => !v)} className="w-full flex items-center justify-between text-sm text-[#8a97a8]" data-testid="legacy-toggle">
          <span>Advanced: use your own Stripe API keys instead</span>
          <ChevronDown size={16} className={`transition-transform ${showLegacy ? "rotate-180" : ""}`} />
        </button>
        {showLegacy && (
          <form onSubmit={saveLegacy} className="space-y-3 mt-4">
            <p className="text-xs text-[#8a97a8]">Only for advanced setups. With Connect above, you don't need this.</p>
            <input value={keys.stripe_pk} onChange={(e) => setKeys({ ...keys, stripe_pk: e.target.value })} placeholder="pk_test_…"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] font-mono text-sm" />
            <input type="password" value={keys.stripe_sk} onChange={(e) => setKeys({ ...keys, stripe_sk: e.target.value })} placeholder="sk_test_…"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] font-mono text-sm" />
            <button disabled={busy === "legacy"} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-60">Save keys</button>
          </form>
        )}
      </div>
    </div>
  );
}
